// Animation presets for 3D text.

import * as THREE from 'three';
import type { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { createTextRig, disposeRig, renderRig, rigZRange, type TextParams, type TextRig } from '../depth/text3d';
import { toDepth16, type Depth16 } from '../types';
import { f32ToBase64, type SceneDesc } from '../scene/types';

export type TextPresetKind = 'turn' | 'swing' | 'tilt' | 'spin' | 'jump' | 'pulse' | 'wave';

export interface TextPreset {
  kind: TextPresetKind;
  /** Degrees for swing/tilt, fraction of text height for jump/wave, scale fraction for pulse. */
  amount: number;
  /** Repetitions per loop (integer keeps the loop seamless). */
  cycles: number;
}

export const TEXT_PRESETS: TextPresetKind[] = ['turn', 'swing', 'tilt', 'spin', 'jump', 'pulse', 'wave'];

export const DEFAULT_PRESET_AMOUNT: Record<TextPresetKind, number> = {
  turn: 0,
  swing: 35,
  tilt: 25,
  spin: 0,
  jump: 0.5,
  pulse: 0.15,
  wave: 0.25,
};

/** Poses the rig for loop phase `t` in 0..1. */
export function applyPreset(rig: TextRig, preset: TextPreset, t: number): void {
  const { pivot } = rig;
  const k = Math.max(1, Math.round(preset.cycles));
  const phase = 2 * Math.PI * k * t;
  pivot.rotation.set(0, 0, 0);
  pivot.position.set(0, 0, 0);
  pivot.scale.setScalar(1);
  const rad = THREE.MathUtils.degToRad;
  switch (preset.kind) {
    case 'turn':
      pivot.rotation.y = phase;
      break;
    case 'swing':
      pivot.rotation.y = rad(preset.amount) * Math.sin(phase);
      break;
    case 'tilt':
      pivot.rotation.x = rad(preset.amount) * Math.sin(phase);
      break;
    case 'spin':
      pivot.rotation.z = -phase;
      break;
    case 'jump':
      pivot.position.z = preset.amount * rig.size.y * Math.abs(Math.sin(phase / 2));
      break;
    case 'pulse':
      pivot.scale.setScalar(1 + preset.amount * Math.sin(phase));
      break;
    case 'wave': {
      // Travelling wave along X, displacing local Z.
      const pos = rig.mesh.geometry.attributes.position as THREE.BufferAttribute;
      const arr = pos.array as Float32Array;
      const bb = rig.mesh.geometry.boundingBox!;
      const width = Math.max(1e-3, bb.max.x - bb.min.x);
      const height = Math.max(1e-3, bb.max.y - bb.min.y);
      const lambda = width / 1.5;
      for (let i = 0; i < arr.length; i += 3) {
        const x = rig.rest[i];
        arr[i + 2] = rig.rest[i + 2] + preset.amount * height * Math.sin((2 * Math.PI * x) / lambda - phase);
      }
      pos.needsUpdate = true;
      rig.mesh.geometry.computeBoundingSphere();
      break;
    }
  }
}

/**
 * Renders `frames` depth frames of animated text. Depth is mapped with one
 * Z range for the whole clip (computed in a first pass) so it does not breathe.
 */
export async function renderTextAnimation(
  font: Font,
  p: TextParams,
  preset: TextPreset,
  width: number,
  height: number,
  frames: number,
  onFrame: (done: number) => void,
  signal?: AbortSignal,
): Promise<Depth16[]> {
  const rig = createTextRig(font, p, width, height);
  try {
    let far = Infinity;
    let near = -Infinity;
    for (let i = 0; i < frames; i++) {
      applyPreset(rig, preset, i / frames);
      const [a, b] = rigZRange(rig);
      far = Math.min(far, a);
      near = Math.max(near, b);
    }
    const out: Depth16[] = [];
    for (let i = 0; i < frames; i++) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      applyPreset(rig, preset, i / frames);
      out.push(toDepth16(renderRig(rig, p, [far, near])));
      onFrame(i + 1);
      // Yield so the UI can paint progress.
      if (i % 2 === 1) await new Promise((r) => setTimeout(r, 0));
    }
    return out;
  } finally {
    disposeRig(rig);
  }
}

/**
 * The same text animation as a SceneDesc (baked mesh + one key per frame on a
 * pivot), so the home server can render it with stereo-render instead of
 * receiving every depth frame. `null` for presets that deform the geometry.
 */
export function textAnimationScene(
  font: Font,
  p: TextParams,
  preset: TextPreset,
  width: number,
  height: number,
  frames: number,
  fps: number,
): SceneDesc | null {
  if (preset.kind === 'wave') return null;
  const rig = createTextRig(font, p, width, height);
  try {
    let far = Infinity;
    let near = -Infinity;
    const keys = [];
    const deg = THREE.MathUtils.radToDeg;
    for (let i = 0; i < frames; i++) {
      applyPreset(rig, preset, i / frames);
      const [a, b] = rigZRange(rig);
      far = Math.min(far, a);
      near = Math.max(near, b);
      const pv = rig.pivot;
      keys.push({
        time: i / fps,
        position: [pv.position.x, pv.position.y, pv.position.z] as [number, number, number],
        rotation: [deg(pv.rotation.x), deg(pv.rotation.y), deg(pv.rotation.z)] as [number, number, number],
        scale: [pv.scale.x, pv.scale.y, pv.scale.z] as [number, number, number],
        easing: 'linear' as const,
      });
    }
    const camZ = 100;
    const m = rig.mesh;
    return {
      version: 1,
      camera: {
        projection: 'orthographic',
        position: [0, 0, camZ],
        target: [0, 0, 0],
        fov: 35,
        height: 2,
        near: camZ - near,
        far: camZ - far,
        base: p.base,
        top: p.top,
      },
      nodes: [
        {
          id: 'pivot',
          name: 'pivot',
          parent: null,
          visible: true,
          transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
          shape: { kind: 'group' },
        },
        {
          id: 'text',
          name: 'text',
          parent: 'pivot',
          visible: true,
          transform: {
            position: [0, 0, 0],
            rotation: [deg(m.rotation.x), deg(m.rotation.y), deg(m.rotation.z)],
            scale: [m.scale.x, m.scale.y, m.scale.z],
          },
          shape: { kind: 'mesh', mesh: 'text' },
        },
      ],
      meshes: [{ id: 'text', positions: f32ToBase64(rig.rest) }],
      timeline: { duration: frames / fps, tracks: [{ target: 'pivot', keys }] },
    };
  } finally {
    disposeRig(rig);
  }
}
